import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { CRMPanel } from './CRMPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Standalone fullscreen CRM page — no admin tabs / chrome, max real-estate for the kanban board.
// Accessed via /admin/crm and the "Pop Out" button inside the regular admin CRM tab.
const AdminCRMPage = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const getStoredPassword = () => localStorage.getItem('adminPassword');

  // Auto-validate any stored password
  useEffect(() => {
    const stored = getStoredPassword();
    if (!stored) { setChecking(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/login`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: stored })
        });
        if (res.ok) setAuthed(true);
        else localStorage.removeItem('adminPassword');
      } catch { /* ignore */ }
      setChecking(false);
    })();
  }, []);

  const handleLogin = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (res.ok) {
        localStorage.setItem('adminPassword', password);
        setAuthed(true);
        toast.success('Welcome back');
      } else toast.error('Invalid password');
    } catch { toast.error('Login failed'); }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a1a14] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0a1a14] flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-white/5 border border-white/10 rounded-2xl p-7">
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#D4AF37]/15 text-[#D4AF37]">
              <Lock className="w-5 h-5" />
            </span>
            <div>
              <h1 className="text-white font-bold text-base">Admin Sign-in</h1>
              <p className="text-white/40 text-xs">Fullscreen CRM</p>
            </div>
          </div>
          <Input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            placeholder="Admin password"
            className="bg-white/5 border-white/10 text-white mb-3"
            data-testid="crm-page-password-input"
            autoFocus
          />
          <Button onClick={handleLogin} className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="crm-page-login-btn">
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1a14] text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-[#0a1a14]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1800px] mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-widest font-semibold transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Site
            </Link>
            <span className="text-white/20">|</span>
            <h1 className="text-white font-bold text-sm flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4 text-[#D4AF37]" /> Lead CRM — Fullscreen
            </h1>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="text-[11px] uppercase tracking-widest text-white/50 hover:text-[#D4AF37] transition-colors font-semibold"
            data-testid="crm-page-back-to-admin"
          >
            Full Admin Panel →
          </button>
        </div>
      </div>

      <div className="max-w-[1800px] mx-auto px-6 py-6">
        <CRMPanel getStoredPassword={getStoredPassword} />
      </div>
    </div>
  );
};

export default AdminCRMPage;
